import React, { useState, useEffect, useRef } from 'react';
import { Mic, Trash2, Play, Square } from 'lucide-react';
import { Button, Table, Dropdown } from 'react-bootstrap';
import MoreVertSharpIcon from '@material-ui/icons/MoreVertSharp';
import swal from 'sweetalert';
import { toast } from "react-toastify";
import AddNewVoiceModal from './add-new-voice-modal';
import axios from '../../../axios';
import apiError from '../../../error';
import Spinner from '../../../component/spinner/spinner';
import './deepfake.css';
import { getDateWithoutTime } from '../../../function';

const formatAudioTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '00:00';
    }

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const VoiceAudioPlayer = ({ src }) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    const handleStart = () => {
        if (!audioRef.current) {
            return;
        }

        audioRef.current.play()
            .then(() => setIsPlaying(true))
            .catch(() => {
                setIsPlaying(false);
            });
    };

    const handleStop = () => {
        if (!audioRef.current) {
            return;
        }

        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setCurrentTime(0);
        setIsPlaying(false);
    };

    const handleProgressChange = (e) => {
        const nextTime = Number(e.target.value);
        if (!audioRef.current || Number.isNaN(nextTime)) {
            return;
        }

        audioRef.current.currentTime = nextTime;
        setCurrentTime(nextTime);
    };

    const remainingTime = Math.max(duration - currentTime, 0);
    const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <div className="voice-audio-player">
            <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
                onTimeUpdate={(e) => setCurrentTime(e.target.currentTime || 0)}
                onEnded={() => setIsPlaying(false)}
                className="voice-audio-element"
            />

            <div className="voice-audio-inline">
                <button
                    type="button"
                    className={`voice-audio-icon-btn ${isPlaying ? 'is-stop' : 'is-start'}`}
                    onClick={isPlaying ? handleStop : handleStart}
                    aria-label={isPlaying ? 'Stop audio' : 'Start audio'}
                    title={isPlaying ? 'Stop' : 'Start'}
                >
                    {isPlaying ? <Square size={14} /> : <Play size={14} />}
                </button>
                <input
                    type="range"
                    className="voice-audio-progress"
                    min={0}
                    max={duration || 0}
                    step={0.1}
                    value={Math.min(currentTime, duration || 0)}
                    onChange={handleProgressChange}
                    style={{
                        background: `linear-gradient(to right, #4e73df ${progressPercent}%, #dbe6ff ${progressPercent}%)`
                    }}
                />
                <span className="voice-audio-remaining-time">-{formatAudioTime(remainingTime)}</span>
            </div>
        </div>
    );
};

const Deepfake = ({ permissibleActionsIAM }) => {
    const [showModal, setShowModal] = useState(false);
    const [voices, setVoices] = useState([]);
    const [loading, setLoading] = useState(false);



    useEffect(() => {
        loadVoices();
    }, []);

    const loadVoices = () => {
        setLoading(true);
        axios.get('/api/deepfake/list-voice')
            .then(res => {
                setVoices(res.data.data.voices || []);
                setLoading(false);
            })
            .catch(err => {
                setLoading(false);
                apiError(err);
            });
    };

    const handleDeleteVoice = (voiceId) => {
        swal({
            title: "Do You Want To Delete This Voice?",
            icon: "warning",
            buttons: ['No', 'Yes'],
            dangerMode: true,
        })
            .then(willDelete => {
                if (willDelete) {
                    setLoading(true);
                    axios.delete(`/api/deepfake/delete-voice/${voiceId}`)
                        .then(() => {
                            toast.success("Voice Deleted Successfully");
                            loadVoices();
                        })
                        .catch(err => {
                            setLoading(false);
                            apiError(err);
                        });
                }
            });
    };

    return (
        <div className="tab-wrap">
            <div className='tabHeading d-flex justify-content-between align-items-center'>
                <div>
                    <h6>AI Voice Manager</h6>
                    <p>Create and manage AI-generated voices for your communication campaigns.</p>
                </div>
                {permissibleActionsIAM['setting-write'] && (
                    <Button
                        variant="success"
                        onClick={() => setShowModal(true)}
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <Mic size={15} className="mr-2" />
                        Add New Voice
                    </Button>
                )}
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner loading={loading} />
                    <p className="mt-2">Loading voices...</p>
                </div>
            ) : voices.length > 0 ? (
                <div className="tableContainerWrapInner mt-4">
                    <Table responsive className="align-middle">
                        <thead className="bg-light">
                            <tr>
                                <th>Voice Name</th>
                                <th>Gender</th>
                                <th>Created Date</th>
                                <th>Preview</th>
                                {permissibleActionsIAM['setting-write'] && <th>Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {voices.map((voice, index) => (
                                <tr key={'voice-' + index}>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            {/* <div className="voice-icon-circle mr-3">
                                                <Mic size={16} />
                                            </div> */}
                                            <span className="font-weight-medium">{voice.name}</span>
                                        </div>
                                    </td>
                                    <td>{voice.gender ?? "Male"}</td>
                                    <td style={{minWidth: '200px'}}>{getDateWithoutTime(voice.createdAt)}</td>
                                    <td>
                                        <VoiceAudioPlayer src={voice.previewUrl} />
                                    </td>
                                    {permissibleActionsIAM['setting-write'] && (
                                        <td>
                                            <Dropdown className="action-dropdown dropdownIcon">
                                                <Dropdown.Toggle className="btn-only" id={`dropdown-voice-${index}`}>
                                                    <MoreVertSharpIcon className="dot-icon" />
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item onClick={() => handleDeleteVoice(voice.voiceId)} className="text-danger">
                                                        Delete
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <div className="mt-5 text-center py-5 border rounded-lg bg-light" style={{ borderStyle: 'dashed' }}>
                    <div className="text-secondary mb-3">
                        <Mic size={48} strokeWidth={1} className="mx-auto" />
                    </div>
                    <h5 className="text-dark">No voices created yet</h5>
                    <p className="text-muted small">Click the "Create Voice" button above to create your first AI voice clone.</p>
                </div>
            )}

            <AddNewVoiceModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                refreshVoices={loadVoices}
            />

        </div>
    );
};

export default Deepfake;
